// Firebase Web SDK v10+ auth. Public config only — read from VITE_FIREBASE_*.
// Real secrets never live client-side.

import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  type Auth,
  type User,
} from "firebase/auth";

// Hosts Life is served from where Firebase Hosting also serves
// /__/auth/handler natively. The auth handshake stays same-origin with
// the page on these hosts (docs/skills/firebase-auth-domain-fix); the
// env value covers localhost and previews. Added 2026-08-02 for the
// www.mydryvnlife.com custom-domain cutover.
const SELF_AUTH_HOSTS = new Set(["dryvn-life.web.app", "www.mydryvnlife.com", "mydryvnlife.com"]);

function resolveAuthDomain(): string | undefined {
  if (typeof window !== "undefined" && SELF_AUTH_HOSTS.has(window.location.hostname)) {
    return window.location.hostname;
  }
  return import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined;
}

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: resolveAuthDomain(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
};

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

export function firebaseApp(): FirebaseApp | null {
  if (typeof window === "undefined") return null;
  if (!config.apiKey || !config.projectId) return null;
  if (_app) return _app;
  _app = getApps()[0] ?? initializeApp(config);
  return _app;
}

export function firebaseAuth(): Auth | null {
  if (_auth) return _auth;
  const app = firebaseApp();
  if (!app) return null;
  _auth = getAuth(app);
  return _auth;
}

export async function getIdToken(): Promise<string | null> {
  const auth = firebaseAuth();
  if (!auth?.currentUser) return null;
  return auth.currentUser.getIdToken();
}

export function subscribeAuth(cb: (user: User | null) => void): () => void {
  const auth = firebaseAuth();
  if (!auth) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
}

export async function signInEmail(email: string, password: string) {
  const auth = firebaseAuth();
  if (!auth) throw new Error("Firebase is not configured.");
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signInGoogle() {
  const auth = firebaseAuth();
  if (!auth) throw new Error("Firebase is not configured.");
  return signInWithPopup(auth, new GoogleAuthProvider());
}

export async function signOut() {
  const auth = firebaseAuth();
  if (!auth) return;
  return fbSignOut(auth);
}
